import { useState, useEffect, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { IDXDisclaimer } from "@/components/compliance";
import OptimizedImage from "@/components/ui/optimized-image";
import { ChevronLeft, ChevronRight, Bed, Bath, Ruler, ExternalLink, TrendingUp } from "lucide-react";

interface SoldListing {
  listingID: string;
  address: string;
  cityName: string;
  state: string;
  zipcode: string;
  bedrooms: number;
  totalBaths: number;
  sqFt: string;
  listingPrice: string;
  soldPrice: number;
  soldDate: string;
  pricePerSqFt: number;
  propType: string;
  propSubType: string;
  listingAgentID: string;
  sellingAgentID: string;
  image: Record<string, { url: string }>;
  fullDetailsURL: string;
  representationType?: "listing" | "buyer" | "both";
}

interface SoldListingsGridProps {
  agentId?: string;
  maxItems?: number;
  showFilters?: boolean;
  showPagination?: boolean;
  title?: string;
  onStatsLoaded?: (stats: { total: number; volume: number }) => void;
}

const ITEMS_PER_PAGE = 50;
const AGENT_ITEMS_PER_PAGE = 6;

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);

const parseSqFt = (val: string | number): number => {
  if (typeof val === "number") return val;
  return parseInt(String(val).replace(/,/g, ""), 10) || 0;
};

const getPhotoUrl = (image: Record<string, { url: string }> | null | undefined): string => {
  if (!image) return "/placeholder.svg";
  const first = image["0"] || Object.values(image)[0];
  return first?.url || "/placeholder.svg";
};

const SoldListingsGrid = ({
  agentId,
  maxItems,
  showFilters = true,
  showPagination = true,
  title,
  onStatsLoaded,
}: SoldListingsGridProps) => {
  const [listings, setListings] = useState<SoldListing[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("soldPrice");
  const [sortDir, setSortDir] = useState("desc");
  const [cityFilter, setCityFilter] = useState("all");
  const [propTypeFilter, setPropTypeFilter] = useState("all");
  const [clientPage, setClientPage] = useState(0);
  const [availableCities, setAvailableCities] = useState<string[]>([]);
  const [availablePropTypes, setAvailablePropTypes] = useState<string[]>([]);

  const fetchListings = async () => {
    setLoading(true);
    try {
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const params = new URLSearchParams({
        offset: String(offset),
        sortBy,
        sortDir,
      });
      if (agentId) params.set("agentId", agentId);
      if (cityFilter !== "all") params.set("city", cityFilter);
      if (propTypeFilter !== "all") params.set("propType", propTypeFilter);

      const res = await fetch(
        `https://${projectId}.supabase.co/functions/v1/idx-soldpending?${params}`,
        {
          headers: {
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
        }
      );
      if (!res.ok) throw new Error("Failed to fetch sold listings");
      const data = await res.json();
      const fetchedListings = data.listings || [];
      const fetchedTotal = data.total || 0;
      setListings(fetchedListings);
      setTotal(fetchedTotal);

      // Use server-provided filter options
      if (data.availableCities) setAvailableCities(data.availableCities);
      if (data.availablePropTypes) setAvailablePropTypes(data.availablePropTypes);
      
      // Compute volume from this page's listings
      const volume = fetchedListings.reduce((sum: number, l: SoldListing) => sum + (l.soldPrice || 0), 0);
      onStatsLoaded?.({ total: fetchedTotal, volume });
    } catch (err) {
      console.error("Error fetching sold listings:", err);
      setListings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchListings();
  }, [offset, sortBy, sortDir, agentId, cityFilter, propTypeFilter]);

  // Reset offset/page when sort or filters change
  useEffect(() => {
    setOffset(0);
    setClientPage(0);
  }, [sortBy, sortDir, cityFilter, propTypeFilter]);

  // For agent mode: paginate client-side from the full filtered set returned by server
  const agentTotalPages = agentId ? Math.ceil(listings.length / AGENT_ITEMS_PER_PAGE) : 0;
  const displayedListings = useMemo(() => {
    if (agentId && !maxItems) {
      const start = clientPage * AGENT_ITEMS_PER_PAGE;
      return listings.slice(start, start + AGENT_ITEMS_PER_PAGE);
    }
    if (maxItems) {
      return listings.slice(0, maxItems);
    }
    return listings;
  }, [listings, agentId, clientPage, maxItems]);

  const totalVolume = useMemo(
    () => listings.reduce((sum, l) => sum + (l.soldPrice || 0), 0),
    [listings]
  );

  const currentPage = Math.floor(offset / ITEMS_PER_PAGE) + 1;
  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);
  const showingStart = offset + 1;
  const showingEnd = Math.min(offset + ITEMS_PER_PAGE, total);

  if (!loading && listings.length === 0 && !agentId) {
    return (
      <div className="text-center py-12">
        <TrendingUp className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h3 className="font-semibold text-lg mb-2">No Sold Properties Found</h3>
        <p className="text-muted-foreground">Check back soon for recently sold listings.</p>
      </div>
    );
  }

  // For agent profiles: hide entirely if no results
  if (!loading && listings.length === 0 && agentId) {
    return null;
  }

  return (
    <div>
      {title && (
        <h2 className="font-heading text-2xl md:text-3xl font-bold mb-2">{title}</h2>
      )}

      {/* Summary */}
      {!loading && listings.length > 0 && (
        <p className="text-muted-foreground mb-6">
          {agentId
            ? `${total} properties sold · ${formatCurrency(totalVolume)} total volume`
            : `Showing ${showingStart}–${showingEnd} of ${total.toLocaleString()} sold listings`}
        </p>
      )}

      {/* Filters & Sort */}
      {showFilters && !loading && listings.length > 0 && (
        <div className="flex flex-wrap gap-3 mb-6">
          <Select value={`${sortBy}-${sortDir}`} onValueChange={(v) => {
            const [s, d] = v.split("-");
            setSortBy(s);
            setSortDir(d);
          }}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="soldPrice-desc">Highest Price</SelectItem>
              <SelectItem value="soldPrice-asc">Lowest Price</SelectItem>
              <SelectItem value="soldDate-desc">Most Recent</SelectItem>
              <SelectItem value="soldDate-asc">Oldest First</SelectItem>
            </SelectContent>
          </Select>

          {availableCities.length > 1 && (
            <Select value={cityFilter} onValueChange={setCityFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Cities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                {availableCities.map((city) => (
                  <SelectItem key={city} value={city}>{city}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {availablePropTypes.length > 1 && (
            <Select value={propTypeFilter} onValueChange={setPropTypeFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {availablePropTypes.map((pt) => (
                  <SelectItem key={pt} value={pt}>{pt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <Skeleton className="h-48 rounded-t-lg rounded-b-none" />
              <CardContent className="p-4 space-y-3">
                <Skeleton className="h-6 w-1/2" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-2/3" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Grid */}
      {!loading && displayedListings.length > 0 && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayedListings.map((listing) => (
            <a
              key={listing.listingID}
              href={listing.fullDetailsURL}
              target="_blank"
              rel="noopener noreferrer"
              className="group block"
            >
              <Card className="overflow-hidden hover:shadow-lg transition-shadow h-full">
                <div className="relative">
                  <OptimizedImage
                    src={getPhotoUrl(listing.image)}
                    alt={`${listing.address}, ${listing.cityName}`}
                    className="w-full h-48 object-cover"
                    variant="card"
                  />
                  <Badge className="absolute top-3 left-3 bg-foreground text-background">
                    SOLD
                  </Badge>
                  {listing.representationType && (
                    <Badge className="absolute top-3 right-3 bg-background/90 text-foreground border border-border">
                      {listing.representationType === "listing"
                        ? "Listed"
                        : listing.representationType === "buyer"
                        ? "Represented Buyer"
                        : "Listed & Represented Buyer"}
                    </Badge>
                  )}
                </div>
                <CardContent className="p-4">
                  {/* Sold Price */}
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-xl font-bold text-foreground">
                      {formatCurrency(listing.soldPrice)}
                    </span>
                    {listing.listingPrice && (
                      <span className="text-sm text-muted-foreground line-through">
                        {listing.listingPrice}
                      </span>
                    )}
                  </div>

                  {/* Address */}
                  <p className="font-medium text-foreground truncate">
                    {listing.address}
                  </p>
                  <p className="text-sm text-muted-foreground mb-3">
                    {listing.cityName}, {listing.state} {listing.zipcode}
                  </p>

                  {/* Specs */}
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                    {listing.bedrooms > 0 && (
                      <span className="flex items-center gap-1">
                        <Bed className="h-4 w-4" /> {listing.bedrooms}
                      </span>
                    )}
                    {listing.totalBaths > 0 && (
                      <span className="flex items-center gap-1">
                        <Bath className="h-4 w-4" /> {listing.totalBaths}
                      </span>
                    )}
                    {parseSqFt(listing.sqFt) > 0 && (
                      <span className="flex items-center gap-1">
                        <Ruler className="h-4 w-4" /> {parseSqFt(listing.sqFt).toLocaleString()} sqft
                      </span>
                    )}
                  </div>

                  {/* Meta row */}
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{listing.propSubType || listing.propType}</span>
                    {listing.soldDate && (
                      <span>
                        Sold {new Date(listing.soldDate).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </span>
                    )}
                  </div>
                  {listing.pricePerSqFt > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      ${listing.pricePerSqFt.toFixed(0)}/sqft
                    </p>
                  )}

                  {/* View Details link */}
                  <div className="mt-3 pt-3 border-t border-border">
                    <span className="text-sm text-primary flex items-center gap-1 group-hover:underline">
                      View Details <ExternalLink className="h-3 w-3" />
                    </span>
                  </div>
                </CardContent>
              </Card>
            </a>
          ))}
        </div>
      )}

      {/* Pagination — server-side for main page, client-side for agent */}
      {showPagination && !loading && !agentId && totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-8">
          <Button
            variant="outline"
            size="sm"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - ITEMS_PER_PAGE))}
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={offset + ITEMS_PER_PAGE >= total}
            onClick={() => setOffset(offset + ITEMS_PER_PAGE)}
          >
            Next <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}
      {showPagination && !loading && agentId && agentTotalPages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-8">
          <Button
            variant="outline"
            size="sm"
            disabled={clientPage === 0}
            onClick={() => setClientPage((p) => Math.max(0, p - 1))}
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {clientPage + 1} of {agentTotalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={clientPage + 1 >= agentTotalPages}
            onClick={() => setClientPage((p) => p + 1)}
          >
            Next <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}

      {/* Disclaimer */}
      {!loading && listings.length > 0 && (
        <div className="mt-8 pt-6 border-t border-border">
          <IDXDisclaimer variant="full" />
        </div>
      )}
    </div>
  );
};

export default SoldListingsGrid;
